import { Divider } from '@mui/material';
import { useState } from 'react';

import { theme } from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import CustomModal from '@/shared/components/custom-modal';

import { StepReview } from './step-review';
import { StepSelectFund } from './step-select-fund';
import { StepSelectIntroducer } from './step-select-introducer';
import { StepSelectUser } from './step-select-user';
import type {
  AttributionTypeSelection,
  ManualAttributionState,
  ManualAttributionStep,
  SelectedFund,
  SelectedIntroducer,
  SelectedUser,
} from './types';

const INITIAL_STATE: ManualAttributionState = {
  introducer: null,
  user: null,
  attributionType: 'General',
  fund: null,
};

export const ManualAttributionModal = () => {
  const { open, handleClose } = useGlobalModal('MANUAL_ATTRIBUTION_MODAL');
  const [step, setStep] = useState<ManualAttributionStep>(1);
  const [state, setState] = useState<ManualAttributionState>(INITIAL_STATE);

  const handleCloseModal = () => {
    setStep(1);
    setState(INITIAL_STATE);
    handleClose();
  };

  const handleSelectIntroducer = (introducer: SelectedIntroducer | null) => {
    setState((prev) => ({ ...prev, introducer }));
  };

  const handleSelectUser = (user: SelectedUser | null) => {
    setState((prev) => ({ ...prev, user }));
  };

  const handleSelectAttributionType = (attributionType: AttributionTypeSelection) => {
    setState((prev) => ({
      ...prev,
      attributionType,
      fund: attributionType === 'General' ? null : prev.fund,
    }));
  };

  const handleSelectFund = (fund: SelectedFund) => {
    setState((prev) => ({ ...prev, fund }));
  };

  const handleNextFromStep2 = () => {
    if (state.attributionType === 'Fund') {
      setStep(3);
    } else {
      setStep(4);
    }
  };

  const handleBackFromStep4 = () => {
    if (state.attributionType === 'Fund') {
      setStep(3);
    } else {
      setStep(2);
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={handleCloseModal}
      title="Manual Attribution"
      withCloseButton
      sx={{
        maxWidth: 560,
        backgroundColor: theme.colors.background.raised,
        maxHeight: 'fit-content',
      }}
    >
      <>
        <Divider
          sx={{
            mt: '16px',
            background: theme.colors.background.overlay,
            borderColor: theme.colors.border.default,
          }}
        />
        {step === 1 && (
          <StepSelectIntroducer
            selected={state.introducer}
            onSelect={handleSelectIntroducer}
            onNext={() => setStep(2)}
            onClose={handleCloseModal}
          />
        )}
        {step === 2 && state.introducer && (
          <StepSelectUser
            introducer={state.introducer}
            selectedUser={state.user}
            attributionType={state.attributionType}
            onSelectUser={handleSelectUser}
            onSelectAttributionType={handleSelectAttributionType}
            onNext={handleNextFromStep2}
            onBack={() => setStep(1)}
          />
        )}
        {step === 3 && (
          <StepSelectFund
            selectedFund={state.fund}
            onSelect={handleSelectFund}
            onNext={() => setStep(4)}
            onBack={() => setStep(2)}
          />
        )}
        {step === 4 && (
          <StepReview state={state} onBack={handleBackFromStep4} onClose={handleCloseModal} />
        )}
      </>
    </CustomModal>
  );
};
